import { head, path } from "ramda"
import PageName from "../../constants/pageName"
import checkCompanyInputValid, { checkOrderInfoValid } from "../checkCompanyInputValid"
import { isNotEmptyString, isNotNullOrEmpty, isset } from "../validate"
import EventType from "./eventType"

export function checkValidPrice(price) {
  return price.isEnabled && price.status === "EFFECTIVE"
}

export function getOrderedPrices(prices, ascending = true) {
  if (!prices?.length) return []
  return [...prices].sort((a, b) => (ascending ? a?.effectiveDateTime?.localeCompare(b?.effectiveDateTime) : b?.effectiveDateTime?.localeCompare(a?.effectiveDateTime)))
}

export function getValidPrice(prices) {
  let targetPrice = null
  getOrderedPrices(prices)?.forEach((price) => {
    if (checkValidPrice(price) && !targetPrice) {
      targetPrice = price
    }
  })
  return targetPrice
}

export function getCurrenciesFromValidPrice(prices) {
  let validPrice = getValidPrice(prices)
  if (validPrice?.currencies) {
    let currencies = []
    Object.entries(validPrice.currencies).forEach(([currencyKey, priceValue]) => {
      if (validPrice || validPrice === 0) currencies.push(currencyKey)
    })
    return currencies
  }
  return null
}

export function getUSDAmount(prices, usdToHKDRate, rmbToUSDRate) {
  let validPrice = getValidPrice(prices)
  let usdPrice = validPrice?.currencies?.USD,
    hkdPrice = validPrice?.currencies?.HKD,
    rmbPrice = validPrice?.currencies?.RMB
  if (usdPrice) {
    return usdPrice
  } else if (hkdPrice) {
    return hkdPrice / usdToHKDRate
  } else if (rmbPrice) {
    return rmbPrice * rmbToUSDRate
  }
  return null
}

export function convertExchangeRate(exchangeRate) {
  return {
    usdToHKDRate: exchangeRate?.["USD-HKD"] || null,
    rmbToUSDRate: exchangeRate?.["RMB-USD"] || null
  }
}

export function processContractAmount(trackData, orderInfo, packageDetails, queries, exchangeRate) {
  let contractCurrency = null
  let contractAmount = null
  const isOrderInfoValid = checkOrderInfoValid(orderInfo)
  const { usdToHKDRate, rmbToUSDRate } = convertExchangeRate(exchangeRate)

  if (isOrderInfoValid && orderInfo.contract?.currency && isset(orderInfo.contract?.total)) {
    // TODO: need to bind order info
    //Check from orderInfo
    //contract total is not really at step 2 if no country/currency query provided
    contractAmount = orderInfo.contract?.total
    contractCurrency = orderInfo.contract.currency
  } else if (isNotNullOrEmpty(packageDetails?.price)) {
    //Check from packageDetails of orderInfo
    contractAmount = packageDetails.price.details?.amount
    const currFromPackagePrice = packageDetails.price.details?.currency
    if (currFromPackagePrice) contractCurrency = currFromPackagePrice
  } else if (queries?.currency && packageDetails?.prices?.length) {
    //check if currency query exists
    const validPrice = getValidPrice(packageDetails.prices)
    if (validPrice.currencies?.[queries.currency]) {
      contractCurrency = queries.currency
      contractAmount = [validPrice.currencies[queries.currency]]
    }
  } else if (packageDetails?.prices?.length) {
    contractCurrency = "USD"
    contractAmount = getUSDAmount(packageDetails.prices, usdToHKDRate, rmbToUSDRate)
  }
  trackData.contractCurrency = contractCurrency
  trackData.contractAmount = contractAmount
}

export function processOrderAmount(trackData, orderInfo, packageDetails, queries, exchangeRate) {
  const isOrderInfoValid = checkOrderInfoValid(orderInfo)
  const { usdToHKDRate, rmbToUSDRate } = convertExchangeRate(exchangeRate)
  let orderAmount = null

  if (isOrderInfoValid && orderInfo?.contract?.total) {
    // TODO: need to bind order info
    orderAmount = [orderInfo.contract.total]
  } else if (isNotNullOrEmpty(packageDetails?.price)) {
    const amount = path(["details", "amount"], packageDetails.price)
    if (amount) {
      orderAmount = [amount]
    }
  } else if (queries?.currency && packageDetails?.prices?.length) {
    const validPrice = getValidPrice(packageDetails.prices)
    if (validPrice.currencies?.[queries.currency]) {
      orderAmount = [validPrice.currencies[queries.currency]]
    }
  } else if (packageDetails?.prices?.length) {
    orderAmount = [getUSDAmount(packageDetails.prices, usdToHKDRate, rmbToUSDRate)]
  }
  trackData.orderAmount = orderAmount
}

export function processNewTotal(trackData, orderInfo, packageDetails, queries, exchangeRate) {
  const isOrderInfoValid = checkOrderInfoValid(orderInfo)
  const { usdToHKDRate, rmbToUSDRate } = convertExchangeRate(exchangeRate)
  let newTotal = null

  const contractCurrencyFromOrder = orderInfo?.contract?.currency
  const contractTotal = orderInfo?.contract?.total
  if (isOrderInfoValid && contractCurrencyFromOrder && isset(contractTotal)) {
    if (contractTotal && contractCurrencyFromOrder === "HKD") {
      newTotal = contractTotal / usdToHKDRate
    } else if (contractTotal && contractCurrencyFromOrder === "RMB") {
      newTotal = contractTotal * rmbToUSDRate
    } else {
      newTotal = contractTotal
    }
  } else if (packageDetails && isNotNullOrEmpty(packageDetails.prices)) {
    newTotal = getUSDAmount(packageDetails.prices, usdToHKDRate, rmbToUSDRate)
  }

  trackData.sourceType = null
  if (queries?.source) trackData.sourceType = queries.source
  if (isOrderInfoValid && orderInfo.salesChannel) trackData.sourceType = orderInfo.salesChannel

  trackData.newTotal = newTotal
  trackData.contractAmountUSD = newTotal
}

export function processCompanyInfo(trackData, orderInfo, companyInfo, paymentInfo) {
  const isOrderInfoValid = checkOrderInfoValid(orderInfo)
  const isCompanyInfoValid = checkCompanyInputValid(companyInfo)
  trackData.companyNameEN = null
  trackData.companyNameSC = null
  trackData.companyNameTC = null
  trackData.companyEmail = null
  trackData.customerFirstName = null
  trackData.customerLastName = null
  trackData.countryID = null
  trackData.phoneCode = null
  trackData.contractID = null
  trackData.orderID = null

  if (isCompanyInfoValid) {
    trackData.companyNameEN = companyInfo.nameEN
    trackData.companyNameSC = companyInfo.nameSC
    trackData.companyNameTC = companyInfo.nameTC
    trackData.companyEmail = companyInfo.email
    trackData.customerFirstName = companyInfo.contactPerson?.firstName
    trackData.customerLastName = companyInfo.contactPerson?.lastName
    trackData.countryID = companyInfo.region?.country?.id
    trackData.phoneCode = companyInfo.telephone?.countryCode?.callCode
  } else if (isOrderInfoValid) {
    trackData.companyNameEN = orderInfo.customer?.nameEN
    trackData.companyNameSC = orderInfo.customer?.nameSC
    trackData.companyNameTC = orderInfo.customer?.nameTC
    trackData.companyEmail = orderInfo.customer?.contact?.email
    trackData.customerFirstName = orderInfo.customer?.contact?.firstName
    trackData.customerLastName = orderInfo.customer?.contact?.lastName
    trackData.countryID = orderInfo.customer?.countryCode
    trackData.phoneCode = orderInfo.customer?.contact?.phoneCountry
  }
  if (isOrderInfoValid) {
    trackData.contractID = orderInfo.contract?.contractID
    trackData.orderID = orderInfo.id
  }
  trackData.paymentMethod = paymentInfo?.paymentMethod || orderInfo?.orderPayment?.paymentDetails?.paymentMethod || null
}

const useTrackObj = (type, { ...trackProps }) => {
  const { queries, companyInfo, orderInfo, packageDetails, exchangeRate, paymentInfo } = trackProps
  console.log({ type, trackProps })

  // sellable items data
  //TODO: need to modify this logic ofr supporting sellable items
  const arrOfSellableItemID = packageDetails?.idForDisplay ? [packageDetails.idForDisplay] : null

  //TODO: need to modify this logic ofr supporting sellable items
  const arrOfSellableItemName = packageDetails?.nameEN ? [packageDetails.nameEN] : null

  //TODO: need to modify this logic ofr supporting sellable items
  const arrOfSellableItemType = isNotEmptyString(packageDetails?.idForDisplay) ? [head(packageDetails.idForDisplay)] : null

  let trackData = {}

  processContractAmount(trackData, orderInfo, packageDetails, queries, exchangeRate)

  processOrderAmount(trackData, orderInfo, packageDetails, queries, exchangeRate)

  processNewTotal(trackData, orderInfo, packageDetails, queries, exchangeRate)

  processCompanyInfo(trackData, orderInfo, companyInfo, paymentInfo)

  let webTrackObj = {}
  // 1.sellable item id
  if (isNotNullOrEmpty(arrOfSellableItemID)) Object.assign(webTrackObj, { spcs_sellitem_id: arrOfSellableItemID })
  // 2.sellable item name
  if (isNotNullOrEmpty(arrOfSellableItemName)) Object.assign(webTrackObj, { spcs_sellitem_name: arrOfSellableItemName })
  // 3.sellable item type
  if (isNotNullOrEmpty(arrOfSellableItemType)) Object.assign(webTrackObj, { spcs_sellitem_type: arrOfSellableItemType })
  // 4.contract amount
  if (trackData.contractAmount) Object.assign(webTrackObj, { spcs_contract_amount: trackData.contractAmount })
  // 5.contract amount usd
  if (trackData.contractAmountUSD) Object.assign(webTrackObj, { spcs_contract_amount_usd: trackData.contractAmountUSD })
  // 6.contract currency
  if (trackData.contractCurrency) Object.assign(webTrackObj, { spcs_contract_currency: trackData.contractCurrency })
  // 7.order amount
  if (isNotNullOrEmpty(trackData.orderAmount)) Object.assign(webTrackObj, { spcs_order_Amount: trackData.orderAmount })
  // 8.source type
  if (isNotEmptyString(trackData.sourceType)) Object.assign(webTrackObj, { spcs_source_type: trackData.sourceType })
  // 9.payment option
  if (trackData.paymentMethod) Object.assign(webTrackObj, { payment_option: trackData.paymentMethod })

  switch (type) {
    case PageName.landing:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        site_subsection: "SPCS Online Subscription Form",
        page_name: "SPCS Online Subscription Form - Basic Profile",
        page_type: "spcs_onlineform",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step01_BasicProfile_EN",
        ...webTrackObj
      }
    case PageName.detail:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        site_subsection: "SPCS Online Subscription Form",
        page_name: "SPCS Online Subscription Form - Detail Profile",
        page_type: "spcs_onlineform",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step02_DetailProfile_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN
      }
    case PageName.summary:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        site_subsection: "SPCS Online Subscription Form",
        page_name: "SPCS Online Subscription Form - Order Summary",
        page_type: "spcs_onlineform",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step03_OrderSummary_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN,
        customer_companyname_tc: trackData.companyNameTC,
        customer_companyname_sc: trackData.companyNameSC,
        customer_first_name: trackData.customerFirstName,
        customer_last_name: trackData.customerLastName,
        customer_country: trackData.countryID,
        customer_country_code: trackData.phoneCode,
        spcs_contract_id: trackData.contractID,
        spcs_order_id: [trackData.orderID]
      }
    case PageName.thankyou:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        site_subsection: "SPCS Online Subscription Form",
        page_name: "SPCS Online Subscription Form - Thank You",
        page_type: "spcs_thankyou",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step04_Thankyou_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN,
        customer_companyname_tc: trackData.companyNameTC,
        customer_companyname_sc: trackData.companyNameSC,
        customer_first_name: trackData.customerFirstName,
        customer_last_name: trackData.customerLastName,
        customer_country: trackData.countryID,
        customer_country_code: trackData.phoneCode,
        spcs_contract_id: trackData.contractID,
        spcs_order_id: [trackData.orderID],
        event_conversion: "1"
      }
    case PageName.failure:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        site_subsection: "SPCS Online Subscription Form",
        page_name: "SPCS Online Subscription Form - Payment Failure",
        page_type: "spcs_onlineform",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step04_PaymentFailure_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN,
        customer_companyname_tc: trackData.companyNameTC,
        customer_companyname_sc: trackData.companyNameSC,
        customer_first_name: trackData.customerFirstName,
        customer_last_name: trackData.customerLastName,
        customer_country: trackData.countryID,
        customer_country_code: trackData.phoneCode,
        spcs_contract_id: trackData.contractID,
        spcs_order_id: [trackData.orderID]
      }
    case PageName.contactSales:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        site_subsection: "SPCS Online Subscription Form",
        page_name: "SPCS Online Subscription Form - Contact Sales",
        page_type: "spcs_onlineform",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_ContactSales_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN
      }
    case EventType.clickContinue:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        event_action: "click",
        event_name: "spcs_continue_click",
        event_title: "SPCS: Continue - Basic Profile (EN)",
        event_category: "SPCSOnClick",
        event_click: "1",
        site_url: "/virtual/source/spcs.htm",
        link_category: "SPCS Online Subscription Form",
        link_name: "Continue - Basic Profile",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step01_BasicProfile_Continue_Button_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN
      }
    case EventType.clickSubmit:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        event_action: "click",
        event_name: "spcs_submit_click",
        event_title: "SPCS: Submit - Detail Profile (EN)",
        event_category: "SPCSOnClick",
        event_click: "1",
        site_url: "/virtual/source/spcs.htm",
        link_category: "SPCS Online Subscription Form",
        link_name: "Submit - Detail Profile",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step02_DetailProfile_Submit_Button_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN,
        customer_companyname_tc: trackData.companyNameTC,
        customer_companyname_sc: trackData.companyNameSC,
        customer_first_name: trackData.customerFirstName,
        customer_last_name: trackData.customerLastName,
        customer_country: trackData.countryID,
        customer_country_code: trackData.phoneCode
      }
    case EventType.clickConfirm:
      return {
        site_category: "Sourcing",
        site_section: "Sales Package Catalogue and Ordering Solution",
        event_action: "click",
        event_name: "spcs_proceedpayment_click",
        event_title: "SPCS: Proceed Payment - Order Summary (EN)",
        event_category: "SPCSOnClick",
        event_click: "1",
        site_url: "/virtual/source/spcs.htm",
        link_category: "SPCS Online Subscription Form",
        link_name: "Proceed Payment - Order Summary",
        event_scenario: "SPCS_Online_Subscription_Form_EN",
        event_step: "SPCS_Online_Subscription_Form_Step03_ProceedPayment_Button_EN",
        ...webTrackObj,
        customer_email: trackData.companyEmail,
        customer_companyname_en: trackData.companyNameEN,
        customer_companyname_tc: trackData.companyNameTC,
        customer_companyname_sc: trackData.companyNameSC,
        customer_first_name: trackData.customerFirstName,
        customer_last_name: trackData.customerLastName,
        customer_country: trackData.countryID,
        customer_country_code: trackData.phoneCode,
        spcs_contract_id: trackData.contractID,
        spcs_order_id: [trackData.orderID]
      }
    default:
      return {}
  }
}

export default useTrackObj
